import React, { useMemo } from "react";
import { parseISO } from "date-fns";
import { useScheduleTemplates } from "@api/scheduleTemplate.api";
import { pluralizeWithCount } from "@common/helpers/string";
import { FiltersTypes } from "@common/types/filters/general";
import { useAccountingTabContext } from "@components/accounting/useAccountingTab";
import { useAccounts } from "@hooks/useAccounts";

interface Props {
    filters: FiltersTypes;
}

function summarizeNames(names: string[], noun: string): string {
    if (names.length === 1) {
        return names[0];
    }
    return pluralizeWithCount(noun, names.length);
}

export const SchedulesReportTemplateFilterLabel: React.FC<Props> = ({
    filters,
}) => {
    const { data: templates } = useScheduleTemplates({
        includeMilestone: true,
    });
    const { scheduleTemplateIds } = filters;
    const selectedIds = scheduleTemplateIds ?? [];
    const names = useMemo(
        () =>
            (templates ?? [])
                .filter((template) =>
                    scheduleTemplateIds?.includes(template.id),
                )
                .map((template) => template.name),
        [templates, scheduleTemplateIds],
    );
    if (selectedIds.length === 0) {
        return null;
    }
    return (
        <span>
            {names.length > 0
                ? summarizeNames(names, "schedule")
                : pluralizeWithCount("schedule", selectedIds.length)}
        </span>
    );
};

export const SchedulesReportGroupFilterLabel: React.FC<Props> = ({
    filters,
}) => {
    const groups = filters.scheduleGroups ?? [];
    if (groups.length === 0) {
        return null;
    }
    return <span>{summarizeNames(groups, "group")}</span>;
};

export const HoldingAccountFilterLabel: React.FC<Props> = ({ filters }) => {
    const { filters: accountingFilters } = useAccountingTabContext();
    const { accounts } = useAccounts({ entityId: accountingFilters.entityId });
    const { holdingAccountIds } = filters;
    const selectedIds = holdingAccountIds ?? [];
    const names = useMemo(
        () =>
            accounts
                .filter((account) => holdingAccountIds?.includes(account.id))
                .map((account) => account.name),
        [accounts, holdingAccountIds],
    );
    if (selectedIds.length === 0) {
        return null;
    }
    return (
        <span>
            {names.length > 0
                ? summarizeNames(names, "account")
                : pluralizeWithCount("account", selectedIds.length)}
        </span>
    );
};

interface TermDateProps {
    from?: string;
    to?: string;
}

export const TermDateFilterLabel: React.FC<TermDateProps> = ({ from, to }) => {
    if (!from && !to) {
        return null;
    }
    const fromLabel = from ? parseISO(from).toLocaleDateString() : undefined;
    const toLabel = to ? parseISO(to).toLocaleDateString() : undefined;
    if (fromLabel && toLabel) {
        return (
            <span>
                {fromLabel} - {toLabel}
            </span>
        );
    }
    return <span>{fromLabel ? `From ${fromLabel}` : `Until ${toLabel}`}</span>;
};
